import { DayPicker, type DateRange } from "react-day-picker";
import { enUS, ptBR } from "react-day-picker/locale";

export type DatePickerProps = {
  /** Selected day "YYYY-MM-DD" (absent = nothing selected). */
  value?: string;
  /** Range end "YYYY-MM-DD" (with `range`). */
  end?: string;
  /** A day was picked; in range mode `end` is set once the second day is picked. */
  onChange: (start: string, end?: string) => void;
  /** Pick a start and an end day. */
  range?: boolean;
  /** The shown month changed (the arrows): its first day "YYYY-MM-01". */
  onMonthChange?: (month: string) => void;
  /** First day of the week (0 = Sunday). */
  weekStartsOn?: number;
  locale?: "en" | "pt-BR";
  testID?: string;
};

function toDate(ymd: string | undefined): Date | undefined {
  if (ymd === undefined) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  return m === null ? undefined : new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function toYmd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

const WEEK = [0, 1, 2, 3, 4, 5, 6] as const;

/** Inline month calendar for picking a day or a range (react-day-picker). Callers put it in a Popover or Sheet. */
export function DatePicker({ value, end, onChange, range, onMonthChange, weekStartsOn = 0, locale = "en", testID }: DatePickerProps) {
  const start = toDate(value);
  const common = {
    locale: locale === "pt-BR" ? ptBR : enUS,
    weekStartsOn: WEEK[Math.max(0, Math.min(6, weekStartsOn))],
    defaultMonth: start,
    showOutsideDays: true,
    fixedWeeks: true,
    onMonthChange: onMonthChange === undefined ? undefined : (m: Date) => onMonthChange(toYmd(m)),
  };
  return (
    <div className="b-datepicker" data-testid={testID}>
      {range === true ? (
        <DayPicker
          {...common}
          mode="range"
          selected={{ from: start, to: toDate(end) }}
          onSelect={(r: DateRange | undefined) => {
            if (r?.from === undefined) return;
            onChange(toYmd(r.from), r.to === undefined || toYmd(r.to) === toYmd(r.from) ? undefined : toYmd(r.to));
          }}
        />
      ) : (
        <DayPicker
          {...common}
          mode="single"
          selected={start}
          onSelect={(d: Date | undefined) => {
            if (d !== undefined) onChange(toYmd(d));
          }}
        />
      )}
    </div>
  );
}
